<template>
    <zen-slide-in
        :isOpen="isOpen"
        title="Add permission"
        :error="error"
        @close="$emit('close')"
    >
        <template #body>
            <div class="space-y-1 mb-6">
                <p class="text-sm text-taupe-600">
                    Assign a role to
                    <strong>{{ userName }}</strong>
                    at a specific scope.
                </p>
            </div>

            <div class="space-y-5">
                <!-- Role -->
                <div>
                    <label
                        for="grant-role"
                        class="block text-sm font-medium leading-6 text-taupe-900"
                    >
                        Role <span class="text-red-500">*</span>
                    </label>
                    <div class="mt-2">
                        <select
                            id="grant-role"
                            :value="data.role"
                            @change="
                                $emit('update:data', {
                                    ...data,
                                    role: $event.target.value,
                                })
                            "
                            class="block w-full rounded-md border-0 py-2 pl-3 pr-10 text-taupe-900 shadow-sm ring-1 ring-inset ring-taupe-300 focus:ring-2 focus:ring-inset focus:ring-sky-600 sm:text-sm sm:leading-6 transition-shadow"
                            data-testid="grant-role-select"
                        >
                            <option value="">Select a role...</option>
                            <option value="admin">Admin</option>
                            <option value="editor">Editor</option>
                            <option value="viewer">Viewer</option>
                        </select>
                    </div>
                    <p class="mt-1 text-xs text-taupe-500">
                        {{ roleDescription }}
                    </p>
                </div>

                <!-- Scope Type -->
                <div>
                    <label
                        for="grant-scope-type"
                        class="block text-sm font-medium leading-6 text-taupe-900"
                    >
                        Scope <span class="text-red-500">*</span>
                    </label>
                    <div class="mt-2">
                        <select
                            id="grant-scope-type"
                            :value="data.scope_type"
                            @change="
                                $emit('update:data', {
                                    ...data,
                                    scope_type: $event.target.value,
                                })
                            "
                            class="block w-full rounded-md border-0 py-2 pl-3 pr-10 text-taupe-900 shadow-sm ring-1 ring-inset ring-taupe-300 focus:ring-2 focus:ring-inset focus:ring-sky-600 sm:text-sm sm:leading-6 transition-shadow"
                            data-testid="grant-scope-select"
                        >
                            <option value="">Select a scope...</option>
                            <option value="account">
                                Account (all projects)
                            </option>
                            <option value="project">Specific project</option>
                            <option
                                value="environment"
                                :disabled="data.role === 'admin'"
                            >
                                Specific environment
                            </option>
                        </select>
                    </div>
                </div>

                <!-- Project Selector -->
                <div
                    v-if="
                        data.scope_type === 'project' ||
                        data.scope_type === 'environment'
                    "
                >
                    <label
                        for="grant-project"
                        class="block text-sm font-medium leading-6 text-taupe-900"
                    >
                        Project <span class="text-red-500">*</span>
                    </label>
                    <div class="mt-2">
                        <zen-auto-complete
                            input-id="grant-project"
                            :model-value="data.selectedProjectUlid"
                            @update:model-value="
                                (value) =>
                                    $emit('update:data', {
                                        ...data,
                                        selectedProjectUlid: value,
                                    })
                            "
                            :options="projects"
                            option-label="name"
                            option-value="ulid"
                            filter-label="name"
                            placeholder="Select a project..."
                            show-dropdown-button
                            :min-length="0"
                            data-testid="grant-project-select"
                        />
                    </div>
                </div>

                <!-- Environment Selector -->
                <div v-if="data.scope_type === 'environment'">
                    <label
                        for="grant-environment"
                        class="block text-sm font-medium leading-6 text-taupe-900"
                    >
                        Environment <span class="text-red-500">*</span>
                    </label>
                    <div class="mt-2">
                        <zen-auto-complete
                            input-id="grant-environment"
                            :model-value="data.selectedEnvironmentUlid"
                            @update:model-value="
                                (value) =>
                                    $emit('update:data', {
                                        ...data,
                                        selectedEnvironmentUlid: value,
                                    })
                            "
                            :options="selectedProjectEnvironments"
                            option-label="name"
                            option-value="ulid"
                            filter-label="name"
                            placeholder="Select an environment..."
                            show-dropdown-button
                            :min-length="0"
                            data-testid="grant-environment-select"
                        />
                    </div>
                </div>
            </div>
        </template>
        <template v-slot:buttons>
            <zen-button
                variant="secondary"
                size="medium"
                @click="$emit('close')"
            >
                Cancel
            </zen-button>
            <zen-button
                variant="primary"
                size="medium"
                :processing="processing"
                :disabled="!canSubmit"
                @click="$emit('confirm')"
                data-testid="submit-grant-button"
            >
                Add Permission
            </zen-button>
        </template>
    </zen-slide-in>
</template>

<script>
export default {
    name: 'AddUserGrantSlideIn',
    props: {
        isOpen: {
            type: Boolean,
            required: true,
        },
        data: {
            type: Object,
            required: true,
        },
        error: {
            type: [String, Object],
            default: null,
        },
        processing: {
            type: Boolean,
            default: false,
        },
        roleDescription: {
            type: String,
            default: '',
        },
        canSubmit: {
            type: Boolean,
            default: false,
        },
        userName: {
            type: String,
            default: '',
        },
        projects: {
            type: Array,
            required: true,
        },
        selectedProjectEnvironments: {
            type: Array,
            required: true,
        },
    },
    emits: ['close', 'confirm', 'update:data'],
}
</script>
